import { Shield, CheckCircle, Play, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface ComplianceGap {
  id: string;
  label: string;
  resolved: boolean;
}

interface ComplianceGateProps {
  gaps: ComplianceGap[];
  onStartEdit?: () => void;
  onFlagForOfficeManager?: () => void;
  isEditing?: boolean;
}

export function ComplianceGate({ gaps, onStartEdit, onFlagForOfficeManager, isEditing }: ComplianceGateProps) {
  const unresolvedGaps = gaps.filter(g => !g.resolved);

  if (unresolvedGaps.length === 0 && gaps.length > 0) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-lg border-2 border-primary/30 bg-primary/5 transition-all duration-500">
        <CheckCircle className="w-5 h-5 text-primary shrink-0" />
        <div>
          <h4 className="text-sm font-bold text-primary">Verification Complete</h4>
          <p className="text-xs text-primary/80 mt-0.5">
            Operative note reconciled against the authorized billing requirements.
          </p>
        </div>
      </div>
    );
  }

  if (gaps.length === 0) return null;

  return (
    <div className="flex flex-col sm:flex-row items-start gap-3 p-3 sm:p-4 rounded-lg border border-amber-300 bg-amber-50/70">
      <Shield className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-1">
          <h4 className="text-sm font-bold text-amber-900">Documentation Suggestions</h4>
        </div>
        <p className="text-xs text-amber-800 mt-1 mb-3">
          {unresolvedGaps.length} item{unresolvedGaps.length !== 1 ? 's' : ''} can be strengthened. Add a suggested line or route to staff; this does not block surgeon sign-off.
        </p>
        <ul className="space-y-1.5 text-xs sm:text-sm">
          {gaps.map((gap) => (
            <li
              key={gap.id}
              className={`flex items-center gap-2 transition-all duration-300 ${
                gap.resolved ? 'text-primary line-through opacity-60' : 'text-amber-900'
              }`}
            >
              <span className={`w-2 h-2 rounded-full shrink-0 ${gap.resolved ? 'bg-primary' : 'bg-amber-500'}`} />
              <span className="break-words">{gap.label}</span>
            </li>
          ))}
        </ul>
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          {!isEditing && onStartEdit && (
            <Button
              onClick={onStartEdit}
              size="sm"
              className="w-full sm:w-auto h-10 px-6 text-sm font-semibold gap-2 bg-amber-500 hover:bg-amber-600 text-white shadow-md animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]"
            >
              <Play className="w-4 h-4" />
              Start Verification
            </Button>
          )}
          {onFlagForOfficeManager && (
            <Button
              onClick={onFlagForOfficeManager}
              size="sm"
              variant="outline"
              className="w-full sm:w-auto h-10 px-6 text-sm font-semibold gap-2 border-amber-300 text-amber-900 hover:bg-amber-100"
            >
              <ClipboardList className="w-4 h-4" />
              Flag for Office Manager
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
